import type {
  AverageMargin,
  CombinationType,
  ComparisonBasis,
  MarginSummaryCategory,
} from 'api/managementReportApi';
import {
  DEFAULT_OTHER_EXPENSE_LABELS,
  OtherExpenseType,
} from 'api/otherExpensesApi';

export const MARGIN_SUMMARY_CATEGORY_ORDER: MarginSummaryCategory[] = [
  'labour',
  'equipment',
  'consumables',
  'otherExpenses',
];

const categoryLabels: Record<MarginSummaryCategory, string> = {
  labour: 'Labour',
  equipment: 'Equipment',
  consumables: 'Consumables',
  otherExpenses: 'Other Expenses',
};

// The margin summary names default categories by camelCase key.
const defaultOtherExpenseLabels: Record<string, string> = {
  parking: DEFAULT_OTHER_EXPENSE_LABELS[OtherExpenseType.PARKING],
  disposal: DEFAULT_OTHER_EXPENSE_LABELS[OtherExpenseType.DISPOSAL],
  specializedTrade:
    DEFAULT_OTHER_EXPENSE_LABELS[OtherExpenseType.SPECIALIZED_TRADE],
  other: DEFAULT_OTHER_EXPENSE_LABELS[OtherExpenseType.OTHER],
};

const combinationTypeLabels: Record<CombinationType, string> = {
  default: 'Default',
  branchSpecific: 'Branch Specific',
  clientSpecific: 'Client Specific',
  branchAndClient: 'Branch + Client',
};

const comparisonBasisLabels: Record<ComparisonBasis, string> = {
  roleDefault: 'Role default',
  notFound: 'Not found',
  branchMatch: 'Branch match',
  defaultRate: 'Default rate',
  configuredMargin: 'Configured margin',
};

export function getMarginSummaryCategoryLabel(
  category: MarginSummaryCategory
): string {
  return categoryLabels[category];
}

export function getCombinationTypeLabel(type: CombinationType): string {
  return combinationTypeLabels[type];
}

export function getComparisonBasisLabel(basis: ComparisonBasis): string {
  return comparisonBasisLabels[basis];
}

export function sortAverageMarginsByCategory(
  summaries: AverageMargin[]
): AverageMargin[] {
  const order = new Map(
    MARGIN_SUMMARY_CATEGORY_ORDER.map((c, i) => [c, i] as const)
  );
  return [...summaries].sort(
    (a, b) =>
      (order.get(a.category) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(b.category) ?? Number.MAX_SAFE_INTEGER)
  );
}

/** camelCase / single word API names → Title Case for display */
export function formatCamelCaseToDisplayLabel(name: string): string {
  const spaced = name.replace(/([A-Z])/g, ' $1').trim();
  return spaced
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function formatMarginDetailDisplayName(
  category: MarginSummaryCategory,
  name: string
): string {
  if (category === 'otherExpenses') {
    // A default category arrives as a camelCase key; a custom category
    // arrives as its own name, which is shown as typed.
    return defaultOtherExpenseLabels[name] ?? name;
  }
  return name;
}
